import React, {ReactElement} from "react";
import {useLocation, useParams} from "react-router";

import {RichTextInput} from "../../components/rich-text-input";
import styles from "./thank-you.module.scss";
import {isParseable} from "../../utility/helpers";
import {SurveyAccessLevel} from "../../models/survey";
import {useQuery} from "@apollo/client";
import {GET_ME, RESPONDENT_PROJECT_COMPLETION_URL} from "../../graphql/queries/queries";
import {Spinner} from "../../components/spinner";
import {MeData} from "../../models/user";
import {DownloadOnTheAppStoreIcon, GetItOnGooglePlayIcon} from "../../components/icons";
import {buildRedirectUrl} from "./url-utils";

interface CustomizedState {
	thankYouMessage: string;
	redirectUrl: string | null;
	accessLevel: SurveyAccessLevel;
}

/**
 * Simple thank you page for when a user completes the survey
 */
const ThankYou = (): ReactElement => {
	const location = useLocation();
	const {data, loading} = useQuery<MeData>(GET_ME);
	// Get params from both window.location.search (before hash) and location.search (after hash)
	// HashRouter only sees params after the hash, so we need to check window.location.search too
	const windowSearchParams = new URLSearchParams(window.location.search);
	const routerSearchParams = new URLSearchParams(location.search);
	// Merge both, with window params taking precedence
	const allSearchParams = new URLSearchParams(routerSearchParams);
	windowSearchParams.forEach((value, key) => {
		allSearchParams.set(key, value);
	});
	const respondentProjectId = allSearchParams.get("respondent_project_id");

	const {data: respondentProjectCompletionUrlData, loading: respondentProjectCompletionUrlLoading} = useQuery(
		RESPONDENT_PROJECT_COMPLETION_URL,
		{
			variables: {
				externalId: respondentProjectId,
			},
			skip: !respondentProjectId,
		}
	);

	const {thankYouMessage, redirectUrl, accessLevel} = location?.state as CustomizedState;

	const completionUrl = respondentProjectCompletionUrlData?.respondentProjectCompletionUrl;

	if (completionUrl) {
		setTimeout(() => {
			window.location.href = completionUrl;
		}, 3000);
	} else if (redirectUrl) {
		setTimeout(() => {
			const finalUrl = buildRedirectUrl({url: redirectUrl, searchParams: allSearchParams});
			window.location.href = finalUrl;
		}, 3000);
	}

	const manualRedirectUrl =
		completionUrl || redirectUrl
			? buildRedirectUrl({url: completionUrl || redirectUrl || "", searchParams: allSearchParams})
			: "";

	if (loading || (respondentProjectId && respondentProjectCompletionUrlLoading)) {
		return (
			<div className={styles.gridContainer}>
				<div className={styles.thanksContainer}>
					<Spinner />
				</div>
			</div>
		);
	}
	return (
		<div className={styles.gridContainer}>
			<div className={styles.thanksContainer}>
				<div className={styles.completed}>
					<svg width="52" height="38" viewBox="0 0 52 38" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M2 20L17 35L50 2" stroke="white" strokeWidth="4" />
					</svg>
				</div>
				<div className={styles.thankYou}>
					{isParseable(thankYouMessage)
						? thankYouMessage && <RichTextInput text={thankYouMessage} />
						: thankYouMessage || "Thank you for responding! 🎉"}
				</div>{" "}
				{(completionUrl || redirectUrl) && (
					<div className={styles.redirecting}>
						You will be redirected to another page momentarily. If you are not redirected automatically,{" "}
						<a href={manualRedirectUrl}>click here.</a>
					</div>
				)}
			</div>
			{!loading && (
				<div className={styles.footerContainer}>
					<span className={styles.footerText}>Do More with Vurvey</span>
					<div className={styles.downloadButtons}>
						<a href="https://apps.apple.com/app/id6472299206" target="_blank" rel="noopener noreferrer">
							<DownloadOnTheAppStoreIcon />
						</a>
						<a
							href="https://play.google.com/store/apps/details?id=com.vurvey.vurveyReviews"
							target="_blank"
							rel="noopener noreferrer"
						>
							<GetItOnGooglePlayIcon />
						</a>
					</div>
				</div>
			)}
		</div>
	);
};

export {ThankYou};
